import type { DocumentInput } from 'urql';

import { queryData } from '@/core/graphqlClient/queryClient';
import { getMarket, getPlatform } from '@/core/settings/utils';
import { SProduct, type SProductFilters } from '@/generated/graphql/types';
import { MabayaProductWithModelId } from '@/productDiscovery/SponsoredProducts/interfaces/MabayaProduct';
import { SponsoredProductsLocationType } from '@/productDiscovery/SponsoredProducts/interfaces/SponsoredProducts';
import { processSponsoredProductsDataByProduct } from '@/productDiscovery/SponsoredProducts/utils/processSponsoredProducts';

import {
  SpDataGoogleShoppingPageTopDocument,
  SpDataGoogleShoppingPageTopQuery,
  SpDataProductPageDocument,
  SpDataProductPageHighlightDocument,
  SpDataProductPageHighlightQuery,
  SpDataProductPageQuery,
  SpDataProductPageTopDocument,
  SpDataProductPageTopQuery,
} from './graphql/SpDataByProduct/generated/spDataByProduct.query.generated';

export const fetchSpDataByProduct = (
  productId: number,
  modelId: number,
  location: SponsoredProductsLocationType,
  device: string,
  queryDocument: DocumentInput,
  abt?: string,
  filters?: SProductFilters,
): Promise<MabayaProductWithModelId[]> => {
  return queryData<
    | SpDataGoogleShoppingPageTopQuery
    | SpDataProductPageTopQuery
    | SpDataProductPageQuery
    | SpDataProductPageHighlightQuery
  >(queryDocument, {
    context: {
      market: getMarket().toUpperCase(),
      platform: getPlatform().toUpperCase(),
      device,
      location,
      abt: abt || null,
    },
    product: {
      productId,
      modelId,
    },
    filters: !filters || Object.keys(filters).length === 0 ? null : filters,
  })
    .then((result) => {
      if (result?.sProducts?.listByProduct?.products) {
        const productsWithChannelOffer =
          result.sProducts.listByProduct.products.filter(
            (p) => !!p.channelOffer?.sku?.modelId,
          );

        return processSponsoredProductsDataByProduct(
          productsWithChannelOffer as SProduct[],
        );
      }
      return [];
    })
    .catch(() => {
      return [];
    });
};

export const fetchSpDataGoogleShoppingPageTop = (
  productId: number,
  modelId: number,
  device: string,
  abt?: string,
  filters?: SProductFilters,
) =>
  fetchSpDataByProduct(
    productId,
    modelId,
    SponsoredProductsLocationType.GOOGLE_SHOPPING_PAGE_TOP,
    device,
    SpDataGoogleShoppingPageTopDocument,
    abt,
    filters,
  );

export const fetchSpDataProductPageTop = (
  productId: number,
  modelId: number,
  device: string,
  abt?: string,
  filters?: SProductFilters,
) =>
  fetchSpDataByProduct(
    productId,
    modelId,
    SponsoredProductsLocationType.PRODUCT_PAGE_TOP,
    device,
    SpDataProductPageTopDocument,
    abt,
    filters,
  );

export const fetchSpDataProductPage = (
  productId: number,
  modelId: number,
  device: string,
  abt?: string,
  filters?: SProductFilters,
) =>
  fetchSpDataByProduct(
    productId,
    modelId,
    SponsoredProductsLocationType.PRODUCT_PAGE,
    device,
    SpDataProductPageDocument,
    abt,
    filters,
  );

export const fetchSpDataProductPageHightlight = (
  productId: number,
  modelId: number,
  device: string,
  abt?: string,
  filters?: SProductFilters,
) =>
  fetchSpDataByProduct(
    productId,
    modelId,
    SponsoredProductsLocationType.PRODUCT_PAGE_HIGHLIGHT,
    device,
    SpDataProductPageHighlightDocument,
    abt,
    filters,
  );
